<script setup>
// NOOVI: resumo de CSAT na Overview — mesma fonte que /reports/csat já usa
// (csat/getMetrics → csat_survey_responses#metrics), reaproveitando o átomo
// CsatMetricCard.vue (não CsatMetrics.vue inteiro, que já traz sua própria
// moldura de card — dentro do OverviewCard duplicaria a borda, mesmo cuidado
// de SlaSummaryContainer.vue). "Satisfação" aqui é a métrica real do backend
// (% de notas 4-5), não a média "X.X/5" ilustrativa do mockup original — o
// backend não calcula média, só a distribuição por nota.
import { computed, inject, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import CsatMetricCard from './CsatMetricCard.vue';
import OverviewCard from './overview/MetricCard.vue';
import {
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
} from '../helpers/overviewDateFilter';

const { t } = useI18n();
const store = useStore();
const dateFilter = inject(
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
  true
);

const metrics = useMapGetter('csat/getMetrics');
const satisfactionScore = useMapGetter('csat/getSatisfactionScore');
const responseRate = useMapGetter('csat/getResponseRate');
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetchingMetrics);

const responseCount = computed(() =>
  metrics.value.totalResponseCount
    ? metrics.value.totalResponseCount.toLocaleString()
    : '0'
);
const formatPercent = value => (value ? `${value}%` : '0%');

// csat/getMetrics não aceita business_hours — o watch só reage a from/to.
const fetchCsatMetrics = () => {
  store.dispatch('csat/getMetrics', {
    from: dateFilter.from,
    to: dateFilter.to,
  });
};

onMounted(fetchCsatMetrics);
watch(() => [dateFilter.from, dateFilter.to], fetchCsatMetrics);
</script>

<template>
  <OverviewCard
    :is-loading="isLoading"
    :loading-message="t('OVERVIEW_REPORTS.CSAT_SUMMARY.LOADING_MESSAGE')"
  >
    <template #header>
      <div class="flex items-center gap-2 flex-row flex-wrap min-w-0">
        <h5 class="mb-0 text-n-slate-12 font-medium text-lg break-words">
          {{ t('OVERVIEW_REPORTS.CSAT_SUMMARY.HEADER') }}
        </h5>
      </div>
    </template>
    <div class="flex sm:flex-row flex-col w-full gap-4 sm:gap-14">
      <CsatMetricCard
        :label="t('CSAT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL')"
        :tooltip="t('CSAT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP')"
        :value="responseCount"
        :is-loading="isLoading"
      />
      <div class="w-full sm:w-px bg-n-strong" />
      <CsatMetricCard
        :label="t('CSAT_REPORTS.METRIC.SATISFACTION_SCORE.LABEL')"
        :tooltip="t('CSAT_REPORTS.METRIC.SATISFACTION_SCORE.TOOLTIP')"
        :value="formatPercent(satisfactionScore)"
        :is-loading="isLoading"
      />
      <div class="w-full sm:w-px bg-n-strong" />
      <CsatMetricCard
        :label="t('CSAT_REPORTS.METRIC.RESPONSE_RATE.LABEL')"
        :tooltip="t('CSAT_REPORTS.METRIC.RESPONSE_RATE.TOOLTIP')"
        :value="formatPercent(responseRate)"
        :is-loading="isLoading"
      />
    </div>
  </OverviewCard>
</template>
